import { graphqlUri, restEndpoint } from "../config"

/**
 * User object passed to the section context.
 */
interface SectionUser {
  id: string
  email: string
  name: string
  role?: string
}

/**
 * Routing integration for promoted prototypes.
 */
interface SectionRouting {
  initialSubRoute: string
  onRouteChange: (subRoute: string) => void
  onBrowserNavigationChange: (callback: (subRoute: string) => void) => void
  navigate: (path: string) => void
}

/**
 * The full section context passed to promoted prototypes.
 * Mirrors the SectionContext type from rapid/shared.
 */
export interface SectionContext {
  subPath: string
  promoted: true
  sectionKey: string
  user: SectionUser
  token: string
  authType: "better-auth"
  tenantFeatures: Record<string, boolean>
  sectionFeatures: Record<string, { enabled: boolean; preview: boolean }>
  tenantCopy: { type: string; name?: string; [key: string]: any } | undefined
  profile: string
  graphqlEndpoint: string
  restEndpoint: string
  routing: SectionRouting
}

/**
 * Props accepted by buildSectionContext, sourced from SectionLoader.
 */
export interface BuildSectionContextProps {
  user: { id: string; email: string; name: string; role?: string }
  token: string | undefined
  sectionFeatures: Record<string, { enabled: boolean; preview: boolean }>
  tenantCopy: { type: string; name?: string; [key: string]: any } | undefined
  tenantFeatures: Record<string, boolean>
  profile: string
  initialSubRoute: string
  onRouteChange: (subRoute: string) => void
  onBrowserNavigationChange: (callback: (subRoute: string) => void) => void
  navigate: (path: string) => void
}

/**
 * Builds a SectionContext from SectionLoader props for a promoted prototype.
 *
 * Normalizes routing: `routing.initialSubRoute` always starts with "/",
 * while `subPath` (backward compat with rapid hub) never starts with "/".
 *
 * @param sectionName - The section key this prototype is replacing
 * @param props - Props from the SectionLoader
 * @returns A complete SectionContext for the promoted prototype
 */
export function buildSectionContext(
  sectionName: string,
  props: BuildSectionContextProps,
): SectionContext {
  const normalizedSubRoute = props.initialSubRoute.startsWith("/")
    ? props.initialSubRoute
    : `/${props.initialSubRoute}`

  return {
    subPath: normalizedSubRoute.slice(1),
    promoted: true,
    sectionKey: sectionName,
    user: {
      id: props.user.id,
      email: props.user.email,
      name: props.user.name,
      role: props.user.role,
    },
    token: props.token ?? "",
    authType: "better-auth",
    tenantFeatures: props.tenantFeatures ?? {},
    sectionFeatures: props.sectionFeatures ?? {},
    tenantCopy: props.tenantCopy,
    profile: props.profile,
    graphqlEndpoint: graphqlUri,
    restEndpoint: restEndpoint,
    routing: {
      initialSubRoute: normalizedSubRoute,
      onRouteChange: props.onRouteChange,
      onBrowserNavigationChange: props.onBrowserNavigationChange,
      navigate: props.navigate,
    },
  }
}
